import { Box, Button, Flex, Text } from '@optiaxiom/react';

import { ProviderConnectionSummary } from '@frontend/types';

import * as styles from './ProviderConnectionGrid.css';

type ProviderGridItemProps = {
  provider: ProviderConnectionSummary;
  onConnect: (providerName: string, scopeBundleId: string) => void;
  onDisconnect: (credentialId: string) => void;
  isConnecting: boolean;
};

export function ProviderGridItem({
  provider,
  onConnect,
  onDisconnect,
  isConnecting,
}: ProviderGridItemProps) {
  const handleConnect = () => {
    onConnect(provider.providerName, provider.scopeBundleId);
  };

  const handleDisconnect = () => {
    if (provider.connection?.id) {
      onDisconnect(provider.connection.id);
    }
  };

  return (
    <Box border="1" borderColor="border.tertiary" rounded="xl" p="16" bg="bg.default" w="224">
      <Flex flexDirection="column" gap="12" w="full">
        <Flex flexDirection="row" alignItems="center" gap="4" w="full">
          <Text fontWeight="500" fontSize="md" color="fg.default">
            {provider.providerName}
          </Text>
          <Text fontSize="sm" color="fg.tertiary">
            {provider.scopeBundleName}
          </Text>
        </Flex>

        {provider.scopeBundleDescription && (
          <Text fontSize="sm" color="fg.tertiary" className={styles.descriptionText}>
            {provider.scopeBundleDescription}
          </Text>
        )}

        <Box>
          {provider.connectionStatus === 'not_connected' ? (
            <Button appearance="default" size="sm" onClick={handleConnect} disabled={isConnecting}>
              {isConnecting ? 'Connecting...' : 'Connect'}
            </Button>
          ) : (
            <Button appearance="default" size="sm" onClick={handleDisconnect}>
              Disconnect
            </Button>
          )}
        </Box>
      </Flex>
    </Box>
  );
}

type ProviderConnectionGridProps = {
  providers: ProviderConnectionSummary[];
  onConnect: (providerName: string, scopeBundleId: string) => void;
  onDisconnect: (credentialId: string) => void;
  isConnecting: boolean;
};

export function ProviderConnectionGrid({
  providers,
  onConnect,
  onDisconnect,
  isConnecting,
}: ProviderConnectionGridProps) {
  return (
    <Flex flexDirection="row" flexWrap="wrap" gap="24" w="full">
      {providers.map(provider => (
        <ProviderGridItem
          key={`${provider.providerId}-${provider.scopeBundleId}`}
          provider={provider}
          onConnect={onConnect}
          onDisconnect={onDisconnect}
          isConnecting={isConnecting}
        />
      ))}
    </Flex>
  );
}
